import { useEffect, useState } from "react";
import type { ActivityPresenceCard, ActivityPresenceMarkResult } from "@verity-connect/contracts";
import { normalizePresenceCode } from "@verity-connect/domain";
import { BrandLockup } from "./BrandLockup";
import { Link } from "./router";
import { forgetPresenceCode, rememberPresenceCode } from "./presence-pending";

type State =
  | { status: "reading" }
  | { status: "marked"; card: ActivityPresenceCard; result: ActivityPresenceMarkResult }
  | { status: "sign-in"; card: ActivityPresenceCard }
  | { status: "waiting"; card: ActivityPresenceCard | null }
  | { status: "typing"; message: string | null }
  | { status: "failed"; message: string };

/**
 * Where a teacher lands after her camera reads the page the presenter held up.
 *
 * The scan is the record. There is no button to press afterwards: she pointed her
 * camera at a page in a room she was standing in, and asking her to confirm that
 * she meant it is a second question about a thing she already did. This screen
 * tells her it is done.
 *
 * An earlier version asked. It also queued the mark on arrival, so the button
 * changed nothing and the two disagreed — the queue had already sent what the
 * button was still asking about. One of them had to go, and the ruling is that
 * the scan carries the authority.
 *
 * The code arrives after the `#`, which browsers never send to a server, so it
 * stays out of request logs on the way in. It is cleared from the address bar at
 * once, which also means a refresh cannot record anything a second time.
 */
export function PresenceLanding() {
  const [state, setState] = useState<State>({ status: "reading" });
  // The session page she tapped Check in on, so the receipt can hand her straight back.
  // Only a path on this site is honoured; anything else is somebody else's link.
  const [backTo] = useState(() => {
    const from = new URLSearchParams(window.location.search).get("from");
    return from && /^\/schedule\/[A-Za-z0-9_-]+$/u.test(from) ? from : null;
  });

  useEffect(() => {
    const raw = new URLSearchParams(window.location.hash.replace(/^#/u, "")).get("c");
    const normalized = raw ? normalizePresenceCode(raw) : null;
    window.history.replaceState(null, "", window.location.pathname);
    if (!normalized) {
      // Either she typed the address herself, or a camera read the square badly.
      // Both end at the same place: a box for the characters printed on the page.
      setState({ status: "typing", message: raw ? "That code was not eight characters." : null });
      return;
    }
    void record(normalized);
  }, []);

  /**
   * Look the code up and record it in one motion.
   *
   * The code is written to this phone before anything is sent. A room with no
   * signal and a teacher who has not signed in yet both end the same way: the
   * mark is already safe here and goes out by itself later.
   */
  const record = async (code: string) => {
    rememberPresenceCode(code);
    setState({ status: "reading" });
    try {
      const lookup = await fetch(`/api/attendee/presence/${encodeURIComponent(code)}`, {
        credentials: "same-origin",
      });
      if (lookup.status === 404) {
        forgetPresenceCode(code);
        /*
         * The server's own sentence, not one written here.
         *
         * Two things arrive as 404 and they are not the same: a code that means
         * nothing, and a seminar that came off the schedule. Saying the first to
         * a teacher holding a real page reads as a broken phone, which she can do
         * nothing about. The server already knows which it is and says so.
         */
        const refusal = await lookup.json().catch(() => null) as { message?: string } | null;
        setState({
          status: "typing",
          message: typeof refusal?.message === "string" && refusal.message
            ? refusal.message
            : "That code does not belong to a session at this convention.",
        });
        return;
      }
      if (!lookup.ok) throw new Error("unavailable");
      const card = await lookup.json() as ActivityPresenceCard;
      if (!card.signedIn) {
        setState({ status: "sign-in", card });
        return;
      }
      const marked = await fetch(`/api/attendee/presence/${encodeURIComponent(code)}/mark`, {
        method: "POST",
        credentials: "same-origin",
      });
      if (!marked.ok) throw new Error("unavailable");
      forgetPresenceCode(code);
      setState({ status: "marked", card, result: await marked.json() as ActivityPresenceMarkResult });
    } catch {
      setState({ status: "waiting", card: null });
    }
  };

  return (
    <div className="page attendee-account-page">
      <section className="attendee-account-card">
        <BrandLockup className="brand-lockup-card" />
        {state.status === "reading" && <p>Recording that you were here…</p>}

        {state.status === "typing" && (
          <>
            <h1 data-page-heading tabIndex={-1}>Scan the QR code to check in.</h1>
            <p>Or enter the eight-character code printed under it.</p>
            <form
              onSubmit={(event) => {
                event.preventDefault();
                const field = new FormData(event.currentTarget).get("code");
                const typed = normalizePresenceCode(typeof field === "string" ? field : "");
                if (!typed) {
                  setState({ status: "typing", message: "A session code is eight characters, printed under the QR code." });
                  return;
                }
                void record(typed);
              }}
            >
              <label>
                <span>Session code</span>
                <input
                  name="code"
                  autoComplete="off"
                  autoCapitalize="characters"
                  spellCheck={false}
                  placeholder="NYRF-M8EF"
                />
              </label>
              {state.message && <p className="form-error">{state.message}</p>}
              <button type="submit" className="primary-action">Check in</button>
            </form>
          </>
        )}

        {state.status === "failed" && (
          <>
            <h1 data-page-heading tabIndex={-1}>That code did not work.</h1>
            <p>{state.message}</p>
            <Link className="secondary-action" href="/">Open the convention guide</Link>
          </>
        )}

        {state.status === "sign-in" && (
          <>
            <h1 data-page-heading tabIndex={-1}>Nearly there.</h1>
            <p className="presence-session-title">{state.card.title}</p>
            {(state.card.room ?? state.card.when) && (
              <p className="presence-session-where">
                {[state.card.room, state.card.when].filter(Boolean).join(" · ")}
              </p>
            )}
            <p>
              Sign in and this goes on your record. Your answer is already saved on this
              phone, so you will not have to scan again.
            </p>
            <Link className="primary-action" href="/sign-in">Sign in</Link>
          </>
        )}

        {state.status === "marked" && (
          <>
            <h1 data-page-heading tabIndex={-1}>
              {state.result.alreadyMarked ? "Already checked in" : "You are checked in."}
            </h1>
            <p className="presence-session-title">{state.card.title}</p>
            {(state.card.room ?? state.card.when) && (
              <p className="presence-session-where">
                {[state.card.room, state.card.when].filter(Boolean).join(" · ")}
              </p>
            )}
            <p>
              {state.result.alreadyMarked
                ? "This seminar was already on your record. Nothing was added twice."
                : state.card.earnsContactHours
                  ? "One contact hour. It is filled in on your continuing-education voucher already."
                  : "This seminar is on your record."}
            </p>
            <Link className="primary-action" href={backTo ?? "/my-wvcea/credit"}>{backTo ? "Back to the session" : "See your voucher"}</Link>
          </>
        )}

        {state.status === "waiting" && (
          <>
            <h1 data-page-heading tabIndex={-1}>Saved on this phone.</h1>
            {state.card && <p className="presence-session-title">{state.card.title}</p>}
            <p>
              The building's signal did not reach us just now. Your answer is held on this
              phone and goes on your record the next time the app opens with a connection.
              There is nothing else to do.
            </p>
            <Link className="secondary-action" href="/">Open the convention guide</Link>
          </>
        )}
      </section>
    </div>
  );
}
